import React, {useEffect, useMemo, useRef, useState} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import {Card} from "../../../../../../../components/card/card.jsx";
import {useParams} from "react-router";
import {dataSetAndEventDidMount, getHeaderTemplate, handleEventClick} from "./utils/work-schedule-calendar-utils.js";
import {useQuery} from "@tanstack/react-query";
import WorkScheduleService from "../../../../../../../services/web/work-schedule-service.js";
import {agGridShiftComparator, formatHolidaysEvent, workScheduleSpecialCases} from "../../utils/work-schedule-utils.js";
import {Template} from "../../../../../../../components/template/template.jsx";
import TemplateHeader from "../../../../../../../components/template/template-header.jsx";
import TemplateBody from "../../../../../../../components/template/template-body.jsx";
import "../../utils/work-schedule.css"
import {FaCircleExclamation} from "react-icons/fa6";
import {Modal} from "../../../../../../../components/modal/modal.jsx";
import SelectComponent from "../../../../../../../components/select/select-component.jsx";
import {FaEraser, FaPrint, FaSearch} from "react-icons/fa";
import Button from "../../../../../../../components/button/button.jsx";
import ColorButton from "../../../../../../../components/color-button/color-button.jsx";
import {isSameDay} from "date-fns";
import projectUtils from "../../../../../../../utils/project-utils.js";
import {translatedKeys} from "../../utils/work-schedule-i18n.js";
import {AgGridReact} from "ag-grid-react";
import './work-schedule-calendar.print.css';
import TeamService from "../../../../../../../services/web/team-service.js";
import aggridUtils from "../../../../../../../utils/aggrid-utils.js";
import JourneyService from "../../../../../../../services/web/journey.service.js";
import HeaderTemplateButton from "../../../../../../../components/button/header-template-button.jsx";
import PrintTemplate from "../../../../../../../components/reports/print-reports/print-template.jsx";
import * as htmlToImage from "html-to-image";

const initialFilters = {
    start: "",
    end: "",
    userIds: [],
    teamIds: [],
    scales: []
};

const localeObj = {
    fullDate: {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    },
    date: {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    },
    time: {
        hour: "2-digit",
        minute: "2-digit"
    }
}

const specialCases = workScheduleSpecialCases;

const WorkScheduleCalendar =  () => {
    const {id} = useParams()
    const [isLoading, setIsLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const calendarRef = useRef(null);
    const [activeFilter, setActiveFilter] = useState("");
    const [filters, setFilters] = useState({...initialFilters, workScheduleId: id,});
    const [showModalInfo, setShowModalInfo] = useState(false);
    const [showModalFilter, setShowModalFilter] = useState(false);
    const [clickedTeam, setClickedTeam] = useState({});
    const [month, setMonth] = useState("");
    const [entity, setEntity] = useState(-1);
    const printButtonRef = useRef(null);
    const [colDefs] = useState([
        {field: "name", headerName: "Nome", flex: 1},
        {field: "nameWar", headerName: "Nome de Guerra", flex: 1},
    ])

    const { data: holidays = [], isLoading: isLoadingHolidays, refetch: refetchHolidayList } = useQuery({
        queryKey: ['holiday'],
        queryFn: async () => {
            const { data } = await WorkScheduleService.getAllHolidays();
            const holidayEvents = []
            data.forEach(holiday => holidayEvents.push(formatHolidaysEvent(holiday)));
            return holidayEvents;
        },
    });

    const {data: userNames, isLoading: isLoadingUsersNames} = useQuery({
        queryFn: () => WorkScheduleService.getUserNames()
            .then(({data}) => data.map(el => ({value: el.id, label: el.text}))),
        queryKey: ["username"],
        enabled: !!id
    });

    const {data: userWarNames, isLoading: isLoadingWarNames} = useQuery({
        queryFn: () => WorkScheduleService.getUserWarNames()
            .then(({data}) => data.map(el => ({value: el.id, label: el.text}))),
        queryKey: ["userWarName"],
        enabled: !!id
    });

    const {data: teams, isLoading: isLoadingTeams } = useQuery({
        queryFn: () => TeamService.getAll()
            .then(({data}) => {
                return data
            }),
        queryKey: ["team"],
        enabled: !!id
    });

    const {data: journeys, isLoading: isLoadingJourneys} = useQuery({
        queryFn: () => JourneyService.getAllJourneys()
            .then(({data}) => data.sort((a,b) => agGridShiftComparator(a.pattern,b.pattern)).map(el => ({value: el.pattern,
                label: specialCases.some(scale => scale.id === el.pattern) ?
                    specialCases.find(scale => scale.id === el.pattern).name :
                    el.pattern})) ?? []),
        queryKey: ['work-schedule-journeys'],
        enabled: !!id
    })

    const toolbar = getHeaderTemplate(id, setShowModalFilter);
    const dateSetAndClickEvent = id ? dataSetAndEventDidMount(setFilters, fetchWorkScheduleEvents, setEvents, setMonth, filters) : null;
    async function fetchWorkScheduleEvents (filters) {
        try {
            setIsLoading(true);
            const response = await WorkScheduleService.getSchedules(filters);
            return response.data.map(item => ({
                title: item.team_name,
                start: item.start_date,
                end: item.end_date,
                display: "block",
                color: item.color,
                extendedProps: {
                    id: item.team_id,
                    users: item.users,
                    scale: item.scale,
                    scheduleName: item.schedule_name
                }
            }));
        } catch (error) {
            console.error("Erro ao buscar eventos do calendário:", error);
            return [];
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if(id) return;

        const allSchedules = JSON.parse(localStorage.getItem("calculated-schedules")) || [];
        const teamSchedulesFiltered = [];
        const schedules = [];

        allSchedules.forEach(el => {
            if(el.schedules.length) {
                const firstUserId = el.schedules[0].userId;
                const filtered = el.schedules.filter(schedule => schedule.userId === firstUserId).map(el2 => ({...el2, color: el.color }));
                if(filtered.length){
                    teamSchedulesFiltered.push([...filtered.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))])
                }
            }
        })

        if(teamSchedulesFiltered.length) {
            teamSchedulesFiltered.forEach(el => {
                el.forEach(s => {
                    schedules.push({ "start": s.startDate, "end": s.endDate, "title": s.name, color: s.color, display: "block" })
                })
            })
        }
        setEvents(schedules);
    }, []);

    useEffect(() => {
        if (!calendarRef.current) return;

        let date = new Date().toISOString().split("T")[0];

        const startDate = JSON.parse(localStorage.getItem("work-schedule-start-date")) || "";
        if(startDate) date = startDate

        const calendarApi = calendarRef.current.getApi();
        calendarApi.gotoDate(date);

    }, [])

    const handleClear = () => {
        const cleanedFilters = {...filters, userIds: [], teamIds: [], scales: [] }
        setActiveFilter("");
        setFilters(cleanedFilters)
        fetchWorkScheduleEvents(cleanedFilters).then(setEvents);
        setShowModalFilter(false)
    }

    const handleSearch = () => {
        fetchWorkScheduleEvents(filters).then(setEvents);
        setShowModalFilter(false)
    }

    const changeColor = (newColor) => {
        setIsLoading(true);

        if (!newColor) {
            projectUtils.swalErrorMessage('', "Não foi possível atualizar a cor", 'Ok');
            setIsLoading(false);
            return;
        }

        try {
            const result = TeamService.updateColor(clickedTeam.id, { color: newColor });
        } catch (error) {
            let message = 'Não foi possível salvar.';
            if (error.response?.status !== 500) {
                message = error.response?.data?.message ?? message;
            }
            projectUtils.swalErrorMessage('', message, 'Ok');
        } finally {
            setIsLoading(false);
        }
    }


    const handleChangeColor = (newColor) => {
        setClickedTeam(prevState => ({...prevState, color: newColor}))
        changeColor(newColor)

        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.getEvents().forEach(event => {
                if (event.title === clickedTeam.name) {
                    event.setProp('color', newColor);
                }
            });
        }
    };

    const teamModalHeader = useMemo(() => {
        if (!clickedTeam.start) return null;

        return (
            <div className="flex items-center gap-2">
                <h3 className="text-17 font-bold">
                    {clickedTeam.start.toLocaleString("pt-BR", localeObj.date)} - {clickedTeam.name}
                </h3>
                <ColorButton color={clickedTeam.color} onChange={handleChangeColor}/>
            </div>
        );
    }, [clickedTeam]);

    return(
        <Template isLoading={isLoading || isLoadingJourneys || isLoadingTeams || isLoadingTeams || isLoadingUsersNames || isLoadingWarNames}>
            <TemplateHeader>
                <div className="flex justify-between w-full">
                    <Template.Title>
                        Calendário
                    </Template.Title>
                   <div className="flex gap-2">
                       {
                         id && calendarRef.current && calendarRef.current.getApi()?.view?.type === "dayGridMonth" &&
                         <div className="flex justify-end gap-2">
                             <HeaderTemplateButton selected={entity} setSelected={setEntity}/>
                         </div>
                       }
                       {
                         id && calendarRef.current && calendarRef.current.getApi()?.view?.type === "dayGridMonth" &&
                         <div className="flex justify-end gap-2">
                             <Button title="Imprimir" onClick={async () => {
                                 const node = document.querySelector(".fc-view-harness");

                                 try {
                                     const dataUrl = await htmlToImage.toPng(node, { pixelRatio: 2 });

                                     const img = new Image();
                                     img.src = dataUrl;

                                     const imageNode = document.getElementById("calendar-image-container");
                                     imageNode.innerHTML = "";
                                     imageNode.appendChild(img);

                                     printButtonRef.current?.print();

                                 } catch (err) {
                                     console.error("Erro ao gerar imagem", err);
                                 }
                             }}>
                                 <FaPrint/>
                             </Button>
                         </div>
                       }
                   </div>
                </div>
            </TemplateHeader>
            <TemplateBody>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-91px)]">
                            <PrintTemplate
                              setIsPrinting={setIsLoading}
                              ref={printButtonRef}
                              entity={entity}
                              title={month}
                              printHeaderClassname="hidden print:block"
                              printFooterClassname="hidden print:block"
                              landscape={true}
                            >
                                <div id="calendar-container" className="work-schedule-full-calendar print:hidden">
                                    <FullCalendar
                                      ref={calendarRef}
                                      plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
                                      initialView="dayGridMonth"
                                      fixedWeekCount={false}
                                      locale={ptBrLocale}
                                      dayMaxEvents={true}
                                      navLinks={false}
                                      editable={false}
                                      displayEventTime={false}
                                      views={{
                                          dayGrid: {
                                              displayEventTime: false
                                          },
                                          list: {
                                              displayEventTime: true
                                          }
                                      }}
                                      height={700}
                                      {...toolbar}
                                      {...dateSetAndClickEvent}
                                      eventClick={id ? info => handleEventClick(info, setClickedTeam, setShowModalInfo) : undefined}
                                      events={[...events, ...holidays]}
                                      eventContent={
                                          (arg) => {
                                              const { isHoliday } = arg.event.extendedProps;
                                              if(isHoliday)
                                                  return (
                                                    <div className="flex items-center gap-1">
                                                        <FaCircleExclamation/>
                                                        <span>{arg.event.title}</span>
                                                    </div>
                                                  );

                                              return true;
                                          }
                                      }
                                      eventDidMount={
                                          (info) => {
                                              if (info.event.classNames.includes('holiday-events')) {
                                                  info.el.className = 'holiday-events';
                                              }
                                          }
                                      }
                                    />
                                </div>
                                <div id="calendar-image-container" className="hidden print:block"></div>
                            </PrintTemplate>
                        </div>
                    </Card.Body>
                </Card>
                <Modal className= "w-8/12" open={showModalFilter} setOpen={setShowModalFilter} title={"Filtros"} overflow={true}>
                    <Modal.Body className="p-4">
                        <div className="flex gap-2">
                            <div className="flex gap-4 w-full">
                                <div className="flex-1">
                                    <SelectComponent
                                        isDisabled={activeFilter && activeFilter !== "user"}
                                        value={userNames?.filter(el => filters.userIds.includes(el.value)) ?? []}
                                        options={userNames ?? []}
                                        label={"Agentes"}
                                        onChange={option => {
                                            setFilters(prevState => ({
                                                ...prevState,
                                                userIds: option.map(o => o.value)
                                            }))
                                            option.length > 0 ? setActiveFilter("user") : setActiveFilter("")
                                        }}
                                        isMulti
                                    />
                                </div>
                                <div className="flex-1">
                                    <SelectComponent
                                        isDisabled={activeFilter && activeFilter !== "user"}
                                        value={userWarNames?.filter(el => filters.userIds.includes(el.value)) ?? []}
                                        options={userWarNames ?? []}
                                        label={"Nome de guerra"}
                                        onChange={option => {
                                            setFilters(prevState => ({
                                                ...prevState,
                                                userIds: option.map(o => o.value)
                                            }))
                                            option.length > 0 ? setActiveFilter("user") : setActiveFilter("")

                                        }}
                                        isMulti
                                    />
                                </div>
                                <div className="flex-1">
                                    <SelectComponent
                                        isDisabled={activeFilter && activeFilter !== "teams"}
                                        value={teams?.filter(el => filters.teamIds.includes(el.value)) ?? []}
                                        options={teams ?? []}
                                        label={"Equipes"}
                                        onChange={option => {
                                            setFilters(prevState => ({
                                                ...prevState,
                                                teamIds: option.map(o => o.value)
                                            }))
                                            option.length > 0 ? setActiveFilter("teams") : setActiveFilter("")

                                        }}
                                        isMulti
                                    />
                                </div>
                                <div className="flex-1 flex gap-4 w-full">
                                    <div className="flex-1">
                                        <SelectComponent
                                          isDisabled={activeFilter && activeFilter !== "journeys"}
                                          value={journeys?.filter(el => filters.scales.includes(el.value)) ?? []}
                                          options={journeys ?? []}
                                          label={"Jornada de trabalho"}
                                          onChange={option => {
                                              setFilters(prevState => ({
                                                  ...prevState,
                                                  scales: option.map(o => o.value)
                                              }))
                                              option.length > 0 ? setActiveFilter("journeys") : setActiveFilter("")
                                          }}
                                          isMulti
                                        />
                                    </div>
                                    <div className="flex gap-2 justify-end items-center">
                                        <Button target="primary-outline" onClick={handleClear}><FaEraser color="#063A6C"/></Button>
                                        <Button onClick={handleSearch}><FaSearch/></Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer></Modal.Footer>
                </Modal>
                <Modal className="w-6/12" open={showModalInfo} setOpen={setShowModalInfo} title={teamModalHeader}>
                    <Modal.Body>
                        <div>
                            <h4 className="font-bold">Informações da equipe</h4>
                            <hr className="border-t border-[#d0d4db]"/>
                            <p>
                                <span>Nome da escala: </span><b>{clickedTeam.scheduleName}</b>
                            </p>
                            <p><span>Jornada de trabalho: </span><b>{translatedKeys[clickedTeam.scale] ?? clickedTeam.scale}</b></p>
                            <p><span>Inicio: </span><b>{clickedTeam.start?.toLocaleString("pt-BR", localeObj.time)}</b></p>
                            <p><span>Fim: </span>
                                <b>
                                    {
                                        isSameDay(clickedTeam.start, clickedTeam.end)
                                            ? clickedTeam.end?.toLocaleString("pt-BR", localeObj.time)
                                            : clickedTeam.end?.toLocaleString("pt-BR", localeObj.fullDate)
                                    }
                                </b>
                            </p>
                        </div>
                        <div style={{width: "100%", marginTop: "8px"}}>
                            <h4 className="font-bold">Agentes</h4>
                            <hr className="border-t border-[#d0d4db]"/>
                            <div className={`mb-2`} style={{height: 200, width: "100%"}}>
                                <AgGridReact
                                    rowData={clickedTeam.users}
                                    columnDefs={colDefs}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme()}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer></Modal.Footer>
                </Modal>
            </TemplateBody>

        </Template>
    )
}

export default WorkScheduleCalendar;